import { z } from 'zod';
import { isoDate, positiveAmount, uuid } from './posting.js';
/**
 * Die Verträge der Eröffnungsposten eines Vorsystems über HTTP — Auftrag
 * `LX-freigabeendpunkt` (2026-10-10), `E-1843`. Grundlage:
 * `docs/superpowers/specs/2026-09-29-lexware-migration-design.md`
 * Abschnitte 9.1 bis 9.3 (`D-30`, `D-51`, `D-57`); Tabelle
 * `eroeffnung_posten` (Migration `0300`); der Dienst ist
 * `apps/api/src/vorsystem/eroeffnung.ts`.
 *
 * **Drei Endpunkte, und keiner legt eine Eröffnung an.** Angelegt werden
 * Eröffnungen ausschliesslich vom Läufer (`pnpm vorsystem:lexware`, Schritt
 * `eroeffnen`) aus dem festgeschriebenen offenen Betrag des Archivbelegs; eine
 * Maske, in die ein Mensch einen Eröffnungsbetrag tippt, gäbe es nur um den
 * Preis einer zweiten, nicht nachgerechneten Zahl. Über HTTP gehen die Liste,
 * die Freigabe (bucht) und die Anlage einer Ausbuchung (bucht nicht).
 *
 * **Die Zustände spiegeln die `CHECK`-Bedingungen aus `0300`**
 * (`eroeffnung_posten_art_bekannt`, `…_richtung_bekannt`, `…_status_bekannt`)
 * und die Typen in `eroeffnung.ts` — dieselben Werte, keine Übersetzung.
 */
/** Eröffnung (aus dem Lauf) oder Ausbuchung (Gegenzeile mit Grund, `D-51`). */
export const eroeffnungspostenArtSchema = z.enum(['eroeffnung', 'ausbuchung']);
/** Rechnung (Forderungskonto im Soll) oder Gutschrift (im Haben) — folgt der Belegart. */
export const eroeffnungspostenRichtungSchema = z.enum(['forderung', 'gutschrift']);
/** `angelegt` → `freigegeben`; die Freigabe ist eine Folgezeile (anfügend, `0300`). */
export const eroeffnungspostenStatusSchema = z.enum(['angelegt', 'freigegeben']);
/**
 * Ein Vorgang der Arbeitsliste `GET /api/v1/vorsystem/eroeffnungsposten` —
 * seine LETZTE Zeile. `id` ist immer die Kennung der **Anlage** und damit der
 * Wert, den Freigabe und Ausbuchung im Pfad erwarten; `zeileId` ist die
 * Kennung der gezeigten Zeile (bei einem freigegebenen Vorgang die Folgezeile).
 *
 * **Kein Kundenname, keine Anschrift** — wie jeder Weg dieser Ära (`E-1685`):
 * Kennung und Nummer des Belegs genügen, um ihn zu finden.
 */
export const eroeffnungspostenZeileSchema = z
    .object({
    id: uuid,
    zeileId: uuid,
    art: eroeffnungspostenArtSchema,
    richtung: eroeffnungspostenRichtungSchema,
    documentId: uuid,
    belegnummer: z.string(),
    /** Echt positiv (`eroeffnung_posten_betrag_positiv`); die Richtung trägt das Vorzeichen. */
    betrag: positiveAmount,
    /** Der Stichtag (`einrichtung.journalbeginn`), auch bei einer Ausbuchung. */
    buchungsdatum: isoDate,
    /** Nur an einer Ausbuchung: die Anlage der Eröffnung, die sie mindert. */
    bezugPostenId: uuid.nullable(),
    begruendung: z.string(),
    status: eroeffnungspostenStatusSchema,
    angelegtVon: uuid,
    angelegtAm: z.string(),
    journalEntryId: uuid.nullable(),
    freigegebenVon: uuid.nullable(),
    freigegebenAm: z.string().nullable(),
    /** Einzelfreigabe (`0263`): der Anleger selbst, nur unter einer Regel ohne vier Augen. */
    einzelfreigabe: z.boolean(),
})
    .strict();
/**
 * `GET /api/v1/vorsystem/eroeffnungsposten` — die volle Antwort.
 *
 * `buchungsform` nennt die geltende Buchungsform der Eröffnung oder `null`,
 * solange die Kanzlei sie nicht beantwortet hat (`LX-6`, `D-57`). Mit `null`
 * weist jede Freigabe mit `VORSYSTEM_BUCHUNGSFORM_OFFEN` ab — eine Oberfläche
 * liest es hier, statt es an einer Ablehnung zu erraten.
 */
export const eroeffnungspostenListeSchema = z
    .object({
    posten: z.array(eroeffnungspostenZeileSchema),
    buchungsform: z.string().nullable(),
})
    .strict();
/**
 * `POST /api/v1/vorsystem/eroeffnungsposten/:id/freigabe` — die Antwort. Kein
 * Rumpf auf der Anfrageseite: Was freigegeben wird, sagt der Pfad, wer
 * freigibt, die Sitzung.
 */
export const eroeffnungspostenFreigabeAntwortSchema = z
    .object({
    id: uuid,
    journalEntryId: uuid,
})
    .strict();
/**
 * `POST /api/v1/vorsystem/eroeffnungsposten/:id/ausbuchung` — die Anlage einer
 * Ausbuchung gegen die Eröffnung im Pfad. **Ein Grund ist Pflicht** (`D-51`,
 * `eroeffnung_posten_begruendung_gefuellt`); ein Grund aus Leerzeichen ist
 * keiner. Das Buchungsdatum steht nicht im Rumpf: Eine Ausbuchung bucht auf
 * das Buchungsdatum ihrer Eröffnung.
 */
export const eroeffnungAusbuchungAnlageSchema = z
    .object({
    betrag: positiveAmount,
    begruendung: z.string().trim().min(1, 'Eine Ausbuchung braucht einen Grund').max(2000),
})
    .strict();
/** Die Antwort der Anlage: die Kennung der neuen Ausbuchung (ihre Anlage). */
export const eroeffnungAusbuchungAntwortSchema = z
    .object({
    id: uuid,
})
    .strict();
//# sourceMappingURL=vorsystem-eroeffnung.js.map